import React, { useRef, useState, forwardRef, useEffect } from 'react';
import classNames from 'classnames';
import { Checkbox } from 'src/common/form/checkbox';
import JourneyDatePicker from 'src/common/form/JourneyDatePicker';
import { MutualActionPlan, MutualActionPlanItem } from 'src/common/interfaces/mutual_action_plan.interface';
import { NeueBasicTextEditor } from '../../components/neue-basic-text-editor';
import { CalendarRegularIcon } from 'src/monet/icons';
import UserRegularIcon from 'src/monet/icons/UserRegularIcon';
import { formatDateTime } from 'src/utils/date';
import { useEditorStore } from '../../editor-store';
import { Block } from '../../types';
import { MutualActionPlanTextInput } from './text-input';
import { functionNoop } from 'src/utils/function/noop';
import { Nullable } from 'src/types/nullable.type';
import { Editor } from '@tiptap/react';
import isNull from 'lodash/isNull';
import { delay } from 'lodash';

type MutualActionPlanActionItemProps = {
  actionPlanUuid: MutualActionPlan['uuid'];
  blockId: Block['id'];
  item: MutualActionPlanItem;
  selected: boolean;
  editable: boolean;
  forceFocusToEnd?: boolean;
  activeStyle?: boolean;
  focussed: boolean;
  dragHandleProps?: any;
  onFocusItem: (item: MutualActionPlanItem) => void;
  onUpdateItem: (item: MutualActionPlanItem, params: any) => void;
  onRemoveItem?: (item: MutualActionPlanItem) => void;
  onVisitorUpdateItem: (item: MutualActionPlanItem, params: any) => void;
};

const INFO_CLASSES = 'rounded-md transition-colors bg-bedrock-light-gray px-2 py-1 text-bedrock-p';
const MARGIN_BETWEEN_ELEMENTS = 16;

export const MutualActionPlanActionItem = forwardRef<HTMLDivElement, MutualActionPlanActionItemProps>(
  (
    {
      actionPlanUuid,
      item,
      blockId,
      selected,
      editable,
      focussed,
      activeStyle,
      forceFocusToEnd,
      dragHandleProps,
      onFocusItem,
      onUpdateItem,
      onRemoveItem = functionNoop,
      onVisitorUpdateItem,
    },
    ref
  ) => {
    const latestSelectionFieldRef = useRef<Nullable<string>>(null);
    const linkClickedAtFieldRef = useRef<Nullable<string>>(null);
    const lastActiveEditorRef = useRef<Nullable<Editor>>(null);
    const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
    const [descriptionHeight, setDescriptionHeight] = useState(0);
    const bottomElementRef = useRef<HTMLDivElement>(null);
    const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
    const clearContextMenuState = useEditorStore((state) => state.clearContextMenuState);
    const [itemCompleted, setItemCompleted] = useState(item.completed || false);
    const [dueDate, setDueDate] = useState(item.due_date || null);
    const [enableRemovingDate, setEnableRemovingDate] = useState(!!item.due_date);
    const [activeMetaSection, setActiveMetaSection] = useState<Nullable<'date' | 'assignee'>>(null);

    useEffect(() => {
      if ((!focussed || !selected) && !isNull(activeMetaSection)) {
        setActiveMetaSection(null);
      }
    }, [selected, focussed, activeMetaSection]);

    const isTextEmpty = (text: any) => {
      // text is a tiptap json object
      if (!text) return true;
      if (text.type === 'doc') {
        if (text.content.length === 0) return true;

        if (text.content.length === 1) {
          const content = text.content[0];
          if (content.type === 'paragraph') {
            if (!content.content) return true;
            if (content.content.length === 0) return true;
            if (content.content.length === 1) {
              const paragraphContent = content.content[0];
              if (paragraphContent.type === 'text') {
                if (paragraphContent.text === '') return true;
              }
            }
          }
        }
      }

      return false;
    };

    const showTopElement = focussed || !isTextEmpty(item.description);
    const showBottomElement = focussed || Boolean(item.assigned_to?.name || dueDate);

    const calculateHeight = () => {
      let totalHeight = 0;
      const bottomElementHeight = bottomElementRef.current?.offsetHeight || 0;

      if (focussed) {
        totalHeight = descriptionHeight + bottomElementHeight + MARGIN_BETWEEN_ELEMENTS;
      } else {
        if (!isTextEmpty(item.description)) {
          totalHeight += descriptionHeight;
        }
        if (showBottomElement) {
          totalHeight += bottomElementHeight;
        }

        if (!isTextEmpty(item.description) && showBottomElement) {
          totalHeight += MARGIN_BETWEEN_ELEMENTS;
        }
      }

      return totalHeight;
    };

    return (
      <div
        className='flex items-start justify-between space-x-2 group/actionitem'
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (editable) {
            setContextMenuState({
              entity: {
                type: 'mutual-action-plan-item',
                item: item,
                mutualActionPlan: {
                  uuid: actionPlanUuid,
                },
                blockId: blockId,
              },
              pageX: e.clientX,
              pageY: e.clientY,
            });
          }
        }}
      >
        <div className='flex flex-1 items-start space-x-4'>
          <Checkbox
            isNeue
            className='w-5 h-5 my-1.5 flex shrink-0 pointer-events-auto'
            checked={itemCompleted}
            onChange={(_, value) => {
              if (!isTextEmpty(item.title)) {
                if (editable) {
                  setItemCompleted(value);
                  onUpdateItem(item, { completed: value });
                } else {
                  // this is an interaction completed by the user on the player, so no dispatch actions
                  setItemCompleted(value);
                  onVisitorUpdateItem(item, { completed: value });
                }
              }
            }}
          />
          <div className='flex flex-col flex-1'>
            <div className='flex flex-1 relative'>
              <MutualActionPlanTextInput
                content={item.title}
                editable={editable}
                completed={itemCompleted}
                selected={selected}
                focusEnd={forceFocusToEnd}
                onTransaction={(editor, hasSelection) => {
                  if (hasSelection) {
                    lastActiveEditorRef.current = editor;
                    latestSelectionFieldRef.current = 'title';
                  }
                }}
                onLinkClick={() => {
                  linkClickedAtFieldRef.current = 'title';
                }}
                onBackspacePressed={(value) => {
                  if (!value) {
                    onRemoveItem(item);
                    dispatchUserEditorAction({
                      type: 'delete-mutual-action-plan-item',
                      item: item,
                      mutualActionPlan: { uuid: actionPlanUuid },
                    });
                    clearContextMenuState();
                  }
                }}
                textTypes={['paragraph', 'link']}
                textClasses={{ paragraph: 'text-bedrock-black pr-4' }}
                textPlaceholder='Task name'
                onUpdate={(content: string, textContent: string) => {
                  onUpdateItem(item, { title: content });
                }}
                onFocus={() => {
                  if (
                    lastActiveEditorRef.current &&
                    latestSelectionFieldRef.current === 'title' &&
                    !linkClickedAtFieldRef.current
                  ) {
                    lastActiveEditorRef.current.commands.setTextSelection(0);
                  }
                  onFocusItem(item);
                }}
                focusOnInit={focussed}
              />
              <div {...dragHandleProps} onMouseDown={(event) => event.stopPropagation()} tabIndex='-1'>
                <div
                  className={classNames(
                    'absolute my-1.5 right-0 opacity-0 group-hover/actionitem:opacity-100 transition-opacity shrink-0 z-[50]',
                    {
                      block: selected && editable,
                      hidden: !selected || !editable,
                    }
                  )}
                >
                  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M7.14293 5.71456C7.9319 5.71456 8.5715 5.07497 8.5715 4.28599C8.5715 3.49702 7.9319 2.85742 7.14293 2.85742C6.35395 2.85742 5.71436 3.49702 5.71436 4.28599C5.71436 5.07497 6.35395 5.71456 7.14293 5.71456ZM12.8572 5.71456C13.6462 5.71456 14.2858 5.07497 14.2858 4.28599C14.2858 3.49702 13.6462 2.85742 12.8572 2.85742C12.0682 2.85742 11.4286 3.49702 11.4286 4.28599C11.4286 5.07497 12.0682 5.71456 12.8572 5.71456ZM8.5715 10.0003C8.5715 10.7893 7.9319 11.4289 7.14293 11.4289C6.35395 11.4289 5.71436 10.7893 5.71436 10.0003C5.71436 9.2113 6.35395 8.57171 7.14293 8.57171C7.9319 8.57171 8.5715 9.2113 8.5715 10.0003ZM12.8572 11.4289C13.6462 11.4289 14.2858 10.7893 14.2858 10.0003C14.2858 9.2113 13.6462 8.57171 12.8572 8.57171C12.0682 8.57171 11.4286 9.2113 11.4286 10.0003C11.4286 10.7893 12.0682 11.4289 12.8572 11.4289ZM8.5715 15.7146C8.5715 16.5035 7.9319 17.1431 7.14293 17.1431C6.35395 17.1431 5.71436 16.5035 5.71436 15.7146C5.71436 14.9256 6.35395 14.286 7.14293 14.286C7.9319 14.286 8.5715 14.9256 8.5715 15.7146ZM12.8572 17.1431C13.6462 17.1431 14.2858 16.5035 14.2858 15.7146C14.2858 14.9256 13.6462 14.286 12.8572 14.286C12.0682 14.286 11.4286 14.9256 11.4286 15.7146C11.4286 16.5035 12.0682 17.1431 12.8572 17.1431Z'
                      fill='currentColor'
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div
              className={classNames('flex flex-col transition-[height] duration-400 overflow-hidden', {
                'mt-2': showTopElement || showBottomElement,
              })}
              style={{ height: !itemCompleted ? `${calculateHeight()}px` : 0 }}
            >
              <div
                className={classNames('transition-colors', {
                  'text-bedrock-black': focussed,
                  'text-neue-gray-4': !focussed,
                  'h-0 invisible': !showTopElement,
                })}
              >
                <MutualActionPlanTextInput
                  content={item.description}
                  editable={editable}
                  selected={selected}
                  textTypes={['paragraph', 'link']}
                  onTransaction={(editor, hasSelection) => {
                    if (hasSelection) {
                      lastActiveEditorRef.current = editor;
                      latestSelectionFieldRef.current = 'description';
                    }
                  }}
                  onLinkClick={() => {
                    linkClickedAtFieldRef.current = 'description';
                  }}
                  textClasses={{ paragraph: 'small !text-inherit' }}
                  textPlaceholder='Description'
                  onUpdate={(content: string, textContent: string) => {
                    onUpdateItem(item, { description: content });
                    // updateItem(item.uuid, { description: content });
                  }}
                  onFocus={() => {
                    if (
                      lastActiveEditorRef.current &&
                      latestSelectionFieldRef.current === 'description' &&
                      !linkClickedAtFieldRef.current
                    ) {
                      lastActiveEditorRef.current.commands.setTextSelection(0);
                    }
                    onFocusItem(item);
                  }}
                  onTextSize={(_, height) => setDescriptionHeight(height)}
                />
              </div>
              <div
                className={classNames('flex justify-start items-center space-x-2', {
                  'mt-4': showTopElement && showBottomElement,
                  'h-0 invisible': !showBottomElement,
                })}
                ref={bottomElementRef}
              >
                {(editable || !!dueDate) && (
                  <>
                    {editable ? (
                      <div className='group/meta-date'>
                        {enableRemovingDate && (
                          <div className='relative'>
                            <div
                              className='absolute top-[7px] left-2 z-50 flex items-center justify-center cursor-pointer'
                              onClick={() => {
                                setEnableRemovingDate(false);
                                setActiveMetaSection(null);
                                onUpdateItem(item, { due_date: null });
                                setDueDate(null);
                              }}
                            >
                              <svg
                                className='transform-gpu w-4 h-4 text-bedrock-black opacity-0 transition-opacity group-hover/meta-date:opacity-100'
                                viewBox='0 0 16 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M12 4L4 12'
                                  stroke='currentColor'
                                  strokeWidth='1.1'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                                <path
                                  d='M4 4L12 12'
                                  stroke='currentColor'
                                  strokeWidth='1.1'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                        <JourneyDatePicker
                          currentDate={dueDate}
                          showTimeSelect={false}
                          isNeue
                          onClick={() => {
                            if (activeMetaSection !== 'date') {
                              setActiveMetaSection('date');
                            }
                          }}
                          customInput={
                            <button
                              type='button'
                              className={classNames(INFO_CLASSES, 'group-hover/meta-date:bg-bedrock-gray-medium', {
                                'group-hover:bg-bedrock-gray-medium': editable && !selected,
                                '!bg-bedrock-gray-medium':
                                  (isNull(activeMetaSection) && activeStyle) || activeMetaSection === 'date',
                              })}
                            >
                              <div
                                className={classNames('flex', {
                                  'text-neue-gray-4': !dueDate,
                                  'text-bedrock-black': dueDate,
                                })}
                              >
                                <CalendarRegularIcon
                                  className={classNames('w-4 h-4 flex shrink-0 text-inherit my-0.5 opacity-100', {
                                    'group-hover/meta-date:opacity-0 transition-opacity': !!dueDate,
                                  })}
                                />
                                <span className='ml-2'>{dueDate ? formatDateTime(dueDate) : 'Due date'}</span>
                              </div>
                            </button>
                          }
                          onChange={(value: string) => {
                            setDueDate(value);
                            setActiveMetaSection(null);
                            onUpdateItem(item, { due_date: value });
                            delay(() => {
                              setEnableRemovingDate(true);
                            }, 300);
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className={classNames(INFO_CLASSES, {
                          'group-hover:bg-bedrock-gray-medium': editable && !selected,
                        })}
                      >
                        <div
                          className={classNames('flex items-center space-x-2', {
                            'text-neue-gray-4': !item.due_date,
                            'text-bedrock-black': item.due_date,
                          })}
                        >
                          <CalendarRegularIcon className='w-4 h-4 flex shrink-0 text-inherit' />
                          <span>{item.due_date ? formatDateTime(item.due_date) : 'Due date'}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {(editable || !!item.assigned_to) && (
                  <div className='group/meta-assignee'>
                    <div
                      className={classNames(INFO_CLASSES, 'flex items-center space-x-2', {
                        'group-hover/meta-assignee:bg-bedrock-gray-medium': editable,
                        'group-hover:bg-bedrock-gray-medium': editable && !selected,
                        '!bg-bedrock-gray-medium':
                          (isNull(activeMetaSection) && activeStyle) || activeMetaSection === 'assignee',
                        'text-neue-gray-4': !item.assigned_to?.name,
                        'text-bedrock-black': item.assigned_to?.name,
                      })}
                    >
                      <UserRegularIcon className='w-4 h-4 flex shrink-0 text-inherit' />
                      <NeueBasicTextEditor
                        content={item.assigned_to ? item.assigned_to.name : ''}
                        editable={editable}
                        selected={selected}
                        textClasses={{ paragraph: classNames('!text-bedrock-p') }}
                        textPlaceholder='Assignee'
                        onUpdate={(content: string, textContent: string) => {
                          onUpdateItem(item, { assigned_to: { name: textContent } });
                        }}
                        onBlur={() => {
                          setActiveMetaSection(null);
                        }}
                        onFocus={() => {
                          setActiveMetaSection('assignee');
                          onFocusItem(item);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
